import { useEffect, useRef, useState } from "react";
import { Play, Pause, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../../lib/api";
import { Slider } from "../ui/slider";
import { Label } from "../ui/label";

export const MusicPicker = ({ cfg, set, canUpload }) => {
  const [data, setData] = useState({ library: [], uploads: [] });
  const [playing, setPlaying] = useState(null);
  const [uploading, setUploading] = useState(false);
  const audio = useRef(null);
  const fileRef = useRef(null);

  const load = () => api.get("/music").then((r) => setData(r.data));
  useEffect(() => { load(); return () => audio.current?.pause(); }, []);

  const toggle = (id) => {
    audio.current?.pause();
    if (playing === id) return setPlaying(null);
    audio.current = new Audio(`${API}/music/file/${id}`);
    audio.current.volume = 0.6;
    audio.current.play();
    audio.current.onended = () => setPlaying(null);
    setPlaying(id);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data: up } = await api.post("/music/upload", fd);
      await load(); set({ music_id: up.id }); toast.success("Pista subida");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const tracks = [...data.library.map((t) => ({ ...t, tag: t.mood })), ...data.uploads.map((t) => ({ ...t, tag: "Tuya" }))];
  const Row = ({ t }) => (
    <div onClick={() => set({ music_id: t.id })} data-testid={`music-track-${t.id}`}
      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${cfg.music_id === t.id ? "border-[#E8C47C]/60 bg-[#E8C47C]/10" : "border-transparent hover:bg-white/5"}`}>
      <button onClick={(e) => { e.stopPropagation(); toggle(t.id); }} className="h-8 w-8 rounded-full bg-white/5 grid place-items-center hover:bg-[#E8C47C]/30" data-testid={`music-play-${t.id}`}>
        {playing === t.id ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </button>
      <div className="flex-1 min-w-0"><p className="text-sm text-white truncate">{t.title}</p><p className="text-[11px] text-zinc-500">{t.tag}</p></div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="music-picker">
      <div onClick={() => set({ music_id: null })} data-testid="music-none"
        className={`p-2.5 rounded-lg text-sm cursor-pointer border ${!cfg.music_id ? "border-[#E8C47C]/60 bg-[#E8C47C]/10 text-white" : "border-transparent text-zinc-400 hover:bg-white/5"}`}>Sin música</div>
      <div className="max-h-56 overflow-y-auto scroll-thin space-y-1">{tracks.map((t) => <Row key={t.id} t={t} />)}</div>
      <input ref={fileRef} type="file" accept="audio/*" hidden onChange={upload} data-testid="music-upload-input" />
      <button disabled={!canUpload || uploading} onClick={() => fileRef.current.click()} data-testid="music-upload-btn"
        className="w-full flex items-center justify-center gap-2 text-sm py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-300 hover:border-[#E8C47C]/50 disabled:opacity-40">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {canUpload ? "Subir mi pista" : "Subir pista (planes de pago)"}
      </button>
      <div className="space-y-2">
        <Label className="text-xs text-zinc-400 flex justify-between">Volumen música <span className="font-mono-tc text-[#E8C47C]">{Math.round(cfg.music_volume * 100)}%</span></Label>
        <Slider data-testid="music-volume-slider" min={0} max={0.6} step={0.01} value={[cfg.music_volume]} onValueChange={([v]) => set({ music_volume: v })} />
      </div>
    </div>
  );
};
